<template>
  <div class="page max-w-[1010px]">
    <div
      class="header text-center md:text-left mx-8 sm:mx-0 mt-5 lg:mt-0"
      data-test="header"
    >
      Overview
    </div>
    <template v-if="donorStore.isDataLoaded">
      <div class="mx-4 lg:ml-0">
        <DonationsSummaryBlock
          class="mb-10"
          data-test="donations-summary-block"
        />
        <div class="middle xl:grid xl:grid-cols-[2fr_3fr] xl:gap-x-12">
          <DonationsGraph
            class="flex xl:order-2"
            data-test="donations-graph"
          ></DonationsGraph>
          <RecentDonations
            class="flex mt-8 xl:mt-0 xl:order-1"
            data-test="recent-donations"
          ></RecentDonations>
        </div>
        <div
          class="flex flex-col justify-center xl:mt-8 mb-8"
          v-if="donorStore.featureCampaigns"
          data-test="feature-campaigns"
        >
          <div class="flex flex-wrap gap-x-5 gap-y-8">
            <div
              class="text-sm weight-600 title mb-2 text-center block w-full my-5"
              data-test="active-fundraisers-title"
            >
              Active Fundraisers
            </div>
            <div
              v-if="donorStore.fundraisers.length === 0 || !hasPublishedFundraisers"
              class="w-full centered-content"
              data-test="no-fundraisers"
            >
              <p class="blank-title">Maximize your impact by creating a fundraiser!</p>
              <p class="paragraph">
                Creating a fundraiser is a great way to engage your peers and help your favorite organizations.
              </p>
              <div>
                <Button
                  v-if="fundraiserStore.fundraiserCreationAllowed"
                  class="p-button-outlined"
                  :style="{ color: '#000' }"
                  @click="() => (fundraiserStore.createFundraiser = true)"
                  data-test="create-fundraiser-button"
                >
                  Create a Fundraiser
                </Button>
              </div>
              <FundraiserCreation
                v-if="fundraiserStore.createFundraiser"
                data-test="fundraiser-creation"
              ></FundraiserCreation>
            </div>
            <FundraiserCard
              v-else
              v-for="fundraiser in sortedFundraisers"
              :key="fundraiser.fundraisers_id"
              :fundraiser="fundraiser"
              :showDetailsButton="false"
              data-test="fundraiser-card"
            />
          </div>
        </div>
      </div>
    </template>
    <WelcomeMessageHolder data-test="welcome-message-holder"></WelcomeMessageHolder>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import DonationsSummaryBlock from './components/DonationsSummaryBlock.vue';
import RecentDonations from './components/RecentDonations.vue';
import DonationsGraph from './components/DonationsGraph.vue';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import FundraiserCard from '@/components/fundraisers/FundraiserCard.vue';
import FundraiserCreation from '../fundraisers/FundraiserCreation.vue';
import WelcomeMessageHolder from '../welcomeMessage/WelcomeMessageHolder.vue';

const authStore = useAuthStore();
const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();

const hasPublishedFundraisers = computed(() => {
  return donorStore.fundraisers.some((fundraiser) => fundraiser.status === 'Published');
});

const sortedFundraisers = computed(() => {
  const sorted = [...donorStore.fundraisers]
    .filter((fundraiser) => fundraiser.status === 'Published')
    .sort((a, b) => new Date(b.enteredDate) - new Date(a.enteredDate))
    .slice(0, 3);
  return sorted;
});

const checkIntroStatus = async () => {
  let seenIntro = localStorage.getItem('seenIntro');
  const oneDayInMs = 24 * 60 * 60 * 1000;
  const enteredDate = new Date(donorStore.donorInfo.enteredDate);
  const now = new Date();
  // Check if `seenIntro` is null and `enteredDate` is within the last day
  if (seenIntro === null) {
    const isRecent = now - enteredDate <= oneDayInMs;

    if (!isRecent) {
      donorStore.newUser = false;
      localStorage.setItem('seenIntro', 'true');
    } else {
      // If `enteredDate` is recent, show the modal
      donorStore.newUser = true;
      localStorage.setItem('seenIntro', 'true');
    }
  } else {
    // `seenIntro` is already set, so skip the modal
    donorStore.newUser = false;
  }

  if (window.localStorage.forceNewUser) {
    donorStore.newUser = true;
    delete window.localStorage.forceNewUser;
  }
};

// onBeforeMount hook to run check on component mount
onBeforeMount(async () => {
  await fundraiserStore.checkFundraiserCreation();
  await checkIntroStatus();
});
</script>
